import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  afterEveryRender,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../../../common/services/notification/notification.service';
import { NotificationCheckModeService } from '../../../common/services/notification-check-mode/notification-check-mode.service';
import { NOTIFICATION_FILTER_TABS } from './notification-full-view.constants';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
} from '../../../common/constants/pagination.constants';
import { EMPLOYEE_ROUTES } from '../../../common/constants/employee.routing.constants';
import {
  Notification,
  NotificationFilterTab,
} from '../../../../ui-components/lib/notification/notifications-type.enum';
import { NotificationComponent } from '../../../../ui-components/lib/notification/notification.component';
import { PaginatorComponent } from '../../../../ui-components/lib/paginator/paginator.component';
import { TableFilterTab } from '../../../../ui-components/lib/table/table.model';
import { TabsComponent } from '../../../../ui-components/lib/tabs/tabs.component';
import { TabComponent } from '../../../../ui-components/lib/tabs/tab/tab.component';
import { SettingIconComponent } from '@kuiper/icons';
import { FEATURE_FLAGS } from '../../../common/constants/feature-flags.constants';
import { FeatureFlagsService } from '../../../common/services/feature-flags/feature-flags.service';
import {
  injectSystemTabsLayout,
  injectFloatingLayout,
  provideSystemTabsLayout,
  injectFloatingPageClasses,
} from '../../../common/helpers/system-tabs-layout/system-tabs-layout';

@Component({
  selector: 'lib-notification-full-view',
  imports: [
    NotificationComponent,
    PaginatorComponent,
    TranslocoPipe,
    TabsComponent,
    TabComponent,
    SettingIconComponent,
  ],
  templateUrl: './notification-full-view.component.html',
  styleUrl: './notification-full-view.component.scss',
  providers: [provideSystemTabsLayout()],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.flex]': 'centerEmptyState()',
    '[class.flex-col]': 'centerEmptyState()',
    '[class.h-full]': 'centerEmptyState()',
  },
})
export class NotificationFullViewComponent implements OnInit, OnDestroy {
  private translocoService = inject(TranslocoService);
  private notificationService = inject(NotificationService);
  private notificationCheckModeService = inject(NotificationCheckModeService);
  private router = inject(Router);
  private readonly featureFlags = inject(FeatureFlagsService);
  private destroy$ = new Subject<void>();
  protected readonly systemTabs = injectSystemTabsLayout();
  protected readonly floating = injectFloatingLayout();
  protected readonly systemTabsPageClasses = injectFloatingPageClasses();
  NotificationFilterTab = NotificationFilterTab;
  notifications = signal<Notification[]>([]);
  isLoading = signal<boolean>(false);
  currentPage = signal<number>(1);
  totalPages = signal<number>(1);
  totalItems = signal<number>(0);
  currentSize = signal<number>(DEFAULT_PAGE_SIZE);
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  private cursorMap = new Map<number, string | null>();

  protected readonly centerEmptyState = computed(
    () => this.floating() && !this.hasVisibleNotifications()
  );

  /** Matches the employee shell height so the empty state can sit in the page. */
  protected readonly emptyPageMinHeight = computed(() =>
    this.featureFlags.getFeatureFlagValue(
      FEATURE_FLAGS.SIDE_MENU.layout_and_behavior
    )
      ? 'min-h-[calc(100dvh-2.5rem)]'
      : 'min-h-[calc(100dvh-7.25rem)]'
  );

  protected readonly notificationHeight = computed(() =>
    this.floating() ? 'auto' : '100%'
  );

  hasVisibleNotifications = computed(() => {
    const activeTab = this.notificationFilterTabs().find(tab => tab.active);
    const isUnread = activeTab?.key === NotificationFilterTab.UNREAD;
    if (isUnread) {
      return this.notifications().some(n => !n.read_at);
    }
    return this.notifications().length > 0;
  });

  hasUnreadNotifications = computed(
    () =>
      this.notifications().some(n => !n.read_at) ||
      (this.notificationFilterTabs().find(
        tab => tab.key === NotificationFilterTab.UNREAD
      )?.count ?? 0) > 0
  );

  private lastMarkAsReadTrigger = 0;
  private lastNewNotificationTrigger = 0;

  notificationFilterTabs = signal<TableFilterTab[]>(
    NOTIFICATION_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.translocoService.translate(tab.label),
    }))
  );

  private notificationEffect = afterEveryRender(() => {
    const markAsReadTrigger = this.notificationService.markAsReadTrigger();
    const newNotificationTrigger =
      this.notificationService.newNotificationTrigger();
    if (markAsReadTrigger !== this.lastMarkAsReadTrigger) {
      this.lastMarkAsReadTrigger = markAsReadTrigger;
      this.loadNotifications();
      this.loadTabsInsight();
    }
    if (newNotificationTrigger !== this.lastNewNotificationTrigger) {
      this.lastNewNotificationTrigger = newNotificationTrigger;
      this.loadNotifications();
      this.loadTabsInsight();
    }
  });

  ngOnInit(): void {
    this.cursorMap.set(1, null);
    this.loadNotifications();
    this.loadTabsInsight();
  }

  loadTabsInsight(): void {
    this.notificationService
      .getTabsInsight()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const activeTab = this.notificationFilterTabs().find(
            tab => tab.active
          );
          const isUnread = activeTab?.key === NotificationFilterTab.UNREAD;
          const totalCount = isUnread
            ? response.data.unread_notifications_count
            : response.data.total_notifications_count;
          this.totalItems.set(totalCount);
          this.totalPages.set(
            Math.max(1, Math.ceil(totalCount / this.currentSize()))
          );

          this.notificationFilterTabs.update(tabs =>
            tabs.map(tab => ({
              ...tab,
              count:
                tab.key === NotificationFilterTab.UNREAD
                  ? response.data.unread_notifications_count
                  : response.data.total_notifications_count,
            }))
          );
        },
      });
  }

  loadNotifications(): void {
    if (this.isLoading()) return;

    const activeTab = this.notificationFilterTabs().find(tab => tab.active);
    const unreadOnly = activeTab?.key === NotificationFilterTab.UNREAD;
    const cursor = this.cursorMap.get(this.currentPage()) ?? null;

    this.isLoading.set(true);
    this.notificationService
      .getNotifications(unreadOnly, cursor, this.currentSize())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.notifications.set(response.data);
          if (response.meta.next_cursor) {
            this.cursorMap.set(
              this.currentPage() + 1,
              response.meta.next_cursor
            );
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  handlePagination(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage()) {
      return;
    }
    this.currentPage.set(page);
    this.loadNotifications();
  }

  changeSize(size: number): void {
    this.currentSize.set(size);
    this.currentPage.set(1);
    this.cursorMap.clear();
    this.cursorMap.set(1, null);
    this.loadNotifications();
    this.loadTabsInsight();
  }

  handleNotificationTabChange(tab: TableFilterTab): void {
    this.notificationFilterTabs.update(tabs =>
      tabs.map(t => ({ ...t, active: t.key === tab.key }))
    );
    this.currentPage.set(1);
    this.cursorMap.clear();
    this.cursorMap.set(1, null);
    this.loadNotifications();
    this.loadTabsInsight();
  }

  handleNotificationClick(notification: Notification): void {
    if (!notification.read_at) {
      this.notificationService
        .markAsRead(notification.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.updateNotificationReadStatus(notification.id);
            this.loadTabsInsight();
          },
        });
    }
  }

  handleMarkAllAsRead(): void {
    this.notificationService
      .markAllAsRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notifications.update(notifications =>
            notifications.map(n => ({
              ...n,
              read_at: new Date().toISOString(),
            }))
          );
          this.loadTabsInsight();
        },
      });
  }

  handleSettingsClick(): void {
    this.router.navigate([EMPLOYEE_ROUTES.ACCOUNT_SETTINGS.PROFILE_DETAILS], {
      queryParams: { tab: 'notifications' },
    });
  }

  handleViewDetailsClick(notification: Notification): void {
    if (!notification.read_at) {
      this.notificationService
        .markAsRead(notification.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.updateNotificationReadStatus(notification.id);
            this.notificationCheckModeService.navigateToNotification(
              notification,
              this.destroy$
            );
          },
          error: () =>
            this.notificationCheckModeService.navigateToNotification(
              notification,
              this.destroy$
            ),
        });
    } else {
      this.notificationCheckModeService.navigateToNotification(
        notification,
        this.destroy$
      );
    }
  }

  private updateNotificationReadStatus(notificationId: number): void {
    this.notifications.update(notifications =>
      notifications.map(n =>
        n.id === notificationId
          ? { ...n, read_at: new Date().toISOString() }
          : n
      )
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
